import { mergeWith } from "lodash-es";
const defaultTitle = "White Label";
const currencyCode = "EGP";
let gtagInitPending;
function initGtag(gtagKey) {
  if (gtagKey && gtagInitPending) {
    return gtagInitPending;
  }
  if (gtagKey) {
    gtagInitPending = new Promise((resolve, reject) => {
      (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": (/* @__PURE__ */ new Date()).getTime(), event: "gtm.js" });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s);
        const dl = l !== "dataLayer" ? "&l=" + l : "";
        j.defer = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        j.onload = resolve;
        j.onerror = reject;
        f.parentNode?.insertBefore(j, f);
      })(window, document, "script", "dataLayer", gtagKey);
      const noscript = document.createElement("noscript");
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtagKey}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.display = "none";
      iframe.style.visibility = "hidden";
      noscript.appendChild(iframe);
      document.body.prepend(noscript);
    });
  }
}
function mapItemData(item) {
  const itemData = {
    id: String(item.sku),
    name: item.name,
    brand: item.brand ? item.brand.url_key : "",
    category: item.categories && item.categories.length ? item.categories[item.categories.length - 1].name : "",
    variant: "",
    price: item.price ? String(item.price?.toFixed(2)) : String(item.unitPrice?.toFixed(2))
  };
  return itemData;
}
const GTAG_EVENTS_MAP = {
  product_impression(data) {
    const listName = data.listName;
    return {
      ecommerce: {
        currencyCode,
        impressions: [
          {
            ...mapItemData(data),
            list: listName
          }
        ]
      }
    };
  },
  product_click(data) {
    const listName = data.listName;
    return {
      event: "productClick",
      ga4: { ...data.ga4 },
      ecommerce: {
        currencyCode,
        click: {
          actionField: { list: listName },
          products: [
            {
              ...mapItemData(data)
            }
          ]
        }
      }
    };
  },
  product_detail_views(data) {
    return {
      event: "product_detail_views",
      ...data.transactionData,
      ga4: { ...data.ga4 },
      ecommerce: {
        currencyCode,
        detail: {
          actionField: { list: data.listName },
          products: [
            {
              ...mapItemData(data.product)
            }
          ]
        }
      }
    };
  },
  product_added_to_cart(data) {
    const listName = data.listname;
    return {
      event: "addToCart",
      ...data.transactionData,
      ga4: { ...data.ga4 },
      ecommerce: {
        currencyCode,
        add: {
          actionField: { list: listName },
          products: [
            {
              ...mapItemData(data.product),
              quantity: data.quantity
            }
          ]
        }
      }
    };
  },
  product_removed_from_cart(data) {
    const listName = data.listname;
    return {
      event: "removeFromCart",
      ga4: { ...data.ga4 },
      ecommerce: {
        currencyCode,
        remove: {
          actionField: { list: listName },
          products: [
            {
              ...mapItemData(data.product),
              quantity: data.quantity
            }
          ]
        }
      }
    };
  },
  checkout_step({ step, items, option }) {
    return {
      event: "checkout",
      ecommerce: {
        currencyCode,
        checkout: {
          actionField: { step, option },
          products: items.map((item) => {
            return {
              ...mapItemData(item),
              quantity: item.quantity
            };
          })
        }
      }
    };
  },
  checkout_step_option({ step, option, items }) {
    return {
      event: "checkoutOption",
      ecommerce: {
        currencyCode,
        checkout: {
          actionField: { step, option },
          products: items.map((item) => {
            return {
              ...mapItemData(item),
              quantity: item.quantity
            };
          })
        }
      }
    };
  },
  purchase({ order, items, listName, transactionData, ga4 }) {
    return {
      event: "purchase",
      ...transactionData,
      ga4,
      ecommerce: {
        currencyCode,
        purchase: {
          actionField: {
            id: order.number,
            affiliation: defaultTitle,
            revenue: String(order.total.toFixed(2)),
            tax: "00.00",
            shipping: String(order.shippingFees.toFixed(2) || "00.00")
          },
          products: items.map((item) => {
            return {
              ...mapItemData(item),
              quantity: item.quantity,
              list: item.listname || listName
            };
          })
        }
      }
    };
  },
  search(data) {
    return {
      event: "Search",
      ...data.transactionData,
      ga4: { ...data.ga4 }
    };
  },
  completing_registration(data) {
    return {
      event: "completingRegistration",
      ...data.transactionData
    };
  },
  add_to_wishlist(data) {
    return {
      event: "addToWishlist",
      ...data.transactionData,
      ga4: { ...data.ga4 }
    };
  }
};
let pendingPush;
let pendingData;
export function gtag(evt, payload) {
  if (!GTAG_EVENTS_MAP[evt]) {
    throw new Error(`Event: ${evt} does not exist`);
  }
  let data = GTAG_EVENTS_MAP[evt](payload);
  function commitDataToLayer() {
    pushLayer(data);
  }
  const canBeGrouped = ["product_impression"].includes(evt.toString());
  if (!canBeGrouped) {
    commitDataToLayer();
    return;
  }
  if (pendingPush) {
    clearTimeout(pendingPush);
    if (pendingData)
      data = mergeWith(data, pendingData, (objValue, srcValue) => {
        if (Array.isArray(objValue)) {
          return [...objValue, ...srcValue];
        }
      });
    pendingData = null;
    pendingPush = null;
  }
  pendingData = data;
  pendingPush = setTimeout(() => {
    pendingData = null;
    commitDataToLayer();
  }, 200);
}
async function pushLayer(...args) {
  await gtagInitPending;
  if (window.dataLayer) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(...args);
  }
}
export default initGtag;
